import React, { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { SAAS_TIERS, TEAM_TIERS, API_PRICING, DATA_PACKS, SELLABLE_STATES } from '../constants';
import { PricingType } from '../types';
import Button from './ui/Button';
import { Check, Building2, ShieldCheck, Sparkles, Loader2, Database, Tag, MapPin, X } from 'lucide-react';

interface UserData {
  id: string;
  email: string;
}

interface StateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedStates: string[]) => void;
  packName: string;
  requiredCount: number;
  loading: boolean;
}

// State Selection Modal Component
const StateSelectionModal: React.FC<StateSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  packName,
  requiredCount,
  loading,
}) => {
  const [selectedStates, setSelectedStates] = useState<string[]>([]);

  if (!isOpen) return null;

  const toggleState = (code: string) => {
    setSelectedStates(prev => {
      if (prev.includes(code)) {
        return prev.filter(s => s !== code);
      }
      if (prev.length >= requiredCount) {
        return prev; // Don't allow more than required
      }
      return [...prev, code];
    });
  };

  const handleConfirm = () => {
    if (selectedStates.length === requiredCount) {
      onConfirm(selectedStates);
    }
  };

  // Calculate total records for selected states
  const selectedRecords = selectedStates.reduce((sum, code) => {
    const state = SELLABLE_STATES.find(s => s.code === code);
    return sum + (state?.records || 0);
  }, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-xl font-bold text-white mb-2">
          Select Your {requiredCount === 1 ? 'State' : `${requiredCount} States`}
        </h3>
        <p className="text-slate-400 text-sm mb-6">
          Choose {requiredCount} state{requiredCount > 1 ? 's' : ''} for your {packName} data pack
        </p>

        {/* Selection counter */}
        <div className="flex items-center justify-between mb-4 p-3 bg-slate-900 rounded-lg">
          <span className="text-slate-300 text-sm">
            Selected: <span className="text-white font-bold">{selectedStates.length}</span> / {requiredCount}
          </span>
          {selectedStates.length > 0 && (
            <span className="text-emerald-400 text-sm">
              {selectedRecords.toLocaleString()} records
            </span>
          )}
        </div>

        {/* State grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          {SELLABLE_STATES.map(state => {
            const isSelected = selectedStates.includes(state.code);
            const isDisabled = !isSelected && selectedStates.length >= requiredCount;

            return (
              <button
                key={state.code}
                onClick={() => toggleState(state.code)}
                disabled={isDisabled}
                className={`
                  p-3 rounded-lg border text-left transition-all
                  ${isSelected
                    ? 'bg-sky-600 border-sky-500 text-white'
                    : isDisabled
                      ? 'bg-slate-900 border-slate-700 text-slate-600 cursor-not-allowed'
                      : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-sky-500 hover:bg-slate-800'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold">{state.code}</span>
                  {isSelected && <Check className="w-4 h-4" />}
                </div>
                <div className="text-xs opacity-75 mt-1">{state.name}</div>
                <div className="text-xs opacity-50">{state.records.toLocaleString()} records</div>
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleConfirm}
            disabled={selectedStates.length !== requiredCount || loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              `Continue to Checkout`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Stripe Price IDs - ProGeoData live prices
const STRIPE_PRICE_IDS = {
  starter_monthly: 'price_1Snj1EB7WWia5jDeYJ4yfvHx',  // $99/mo - 5,000 credits
  starter_annual: 'price_1Snj1EB7WWia5jDeYJ4yfvHx',   // TODO: Create annual price
  growth_monthly: 'price_1Snj1FB7WWia5jDeKiouHbnh',   // $249/mo - 15,000 credits
  growth_annual: 'price_1Snj1FB7WWia5jDeKiouHbnh',    // TODO: Create annual price
  agency_monthly: 'price_1Snj1FB7WWia5jDeZSKnFbyZ',   // $499/mo - 30,000 credits
  agency_annual: 'price_1Snj1FB7WWia5jDeZSKnFbyZ',    // TODO: Create annual price
};

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<PricingType>(PricingType.SAAS);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState<string>('HOLIDAY50');
  const [user, setUser] = useState<UserData | null>(null);

  // State selection modal state
  const [stateModalOpen, setStateModalOpen] = useState(false);
  const [pendingPack, setPendingPack] = useState<{ id: string; name: string; statesCount: number } | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('progeodata_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch { /* ignore */ }
    }
  }, []);

  const handleCheckout = async (tierName: string, selectedStates?: string[]) => {
    setError(null);

    // Free tier: navigate to registration
    if (tierName === 'Free') {
      navigate({ to: '/auth', search: { mode: 'register' } });
      return;
    }

    // Check if this is a data pack that requires state selection
    const dataPack = DATA_PACKS.find(p => p.id === tierName);
    const requiresStateSelection = dataPack && (tierName === 'single_state' || tierName === 'regional');

    // If state selection required and no states provided, open modal
    if (requiresStateSelection && !selectedStates) {
      setPendingPack({
        id: tierName,
        name: dataPack.name,
        statesCount: dataPack.states_count,
      });
      setStateModalOpen(true);
      return;
    }

    setLoadingTier(tierName);

    try {
      // Determine the correct price ID based on tier and billing interval
      const tierKey = tierName.toLowerCase();
      let priceId = '';

      // Check if it's a Data Pack
      if (dataPack) {
        priceId = dataPack.stripePriceId;
      } else {
        // It's a subscription
        const interval = billingInterval === 'annual' ? 'annual' : 'monthly';
        const priceIdKey = `${tierKey}_${interval}` as keyof typeof STRIPE_PRICE_IDS;
        priceId = STRIPE_PRICE_IDS[priceIdKey];
      }

      if (!priceId) {
        throw new Error('Invalid pricing tier or missing price ID');
      }

      // Call the subscribe API endpoint
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          tier: tierName,
          couponCode: couponCode || undefined,
          userId: user?.id,
          email: user?.email,
          statesSelected: selectedStates,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || 'Checkout failed');
      }

      const data = await response.json() as { checkoutUrl?: string };

      // Redirect to Stripe Checkout
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setLoadingTier(null);
    }
  };

  const handleStateSelectionConfirm = (selectedStates: string[]) => {
    if (pendingPack) {
      handleCheckout(pendingPack.id, selectedStates);
    }
  };

  const handleStateModalClose = () => {
    setStateModalOpen(false);
    setPendingPack(null);
    setLoadingTier(null);
  };

  const handleContactSales = () => {
    // Scroll to contact form or open a modal
    window.location.href = 'mailto:sales@progeodata.com?subject=Enterprise Inquiry';
  };

  return (
    <section id="pricing" className="py-24 bg-slate-900 relative border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-6">Premium Data. Fair Terms.</h2>

          {error && (
            <div className="mb-6 mx-auto max-w-2xl p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
              {error}
            </div>
          )}

          {/* Main Toggle */}
          <div className="inline-flex bg-slate-800 border border-slate-700 rounded-lg p-1 mb-8">
            <button
              onClick={() => setActiveView(PricingType.SAAS)}
              className={`px-6 py-2 rounded-md text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-sky-500 ${activeView === PricingType.SAAS ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                }`}
            >
              Subscription Plans
            </button>
            <button
              onClick={() => setActiveView(PricingType.API)}
              className={`px-6 py-2 rounded-md text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-sky-500 ${activeView === PricingType.API ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                }`}
            >
              Pay-Per-Request API
            </button>
            <button
              onClick={() => setActiveView(PricingType.DATA_PACKS)}
              className={`px-6 py-2 rounded-md text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-sky-500 ${activeView === PricingType.DATA_PACKS ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                }`}
            >
              Data Packs
            </button>
          </div>

          {/* Billing Cycle Toggle (Only for SAAS) */}
          {activeView === PricingType.SAAS && (
            <div className="flex items-center justify-center gap-4 mb-8 animate-fade-in-up">
              <span className={`text-sm font-bold ${billingInterval === 'monthly' ? 'text-white' : 'text-slate-400'}`}>Monthly</span>
              <button
                onClick={() => setBillingInterval(prev => prev === 'monthly' ? 'annual' : 'monthly')}
                className="relative inline-flex h-6 w-11 items-center rounded-full bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                <span className={`${billingInterval === 'annual' ? 'translate-x-6 bg-emerald-400' : 'translate-x-1 bg-slate-400'} inline-block h-4 w-4 transform rounded-full transition-transform`} />
              </button>
              <span className={`text-sm font-bold flex items-center gap-2 ${billingInterval === 'annual' ? 'text-white' : 'text-slate-400'}`}>
                Annual
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg">
                  <Sparkles className="w-3 h-3 mr-1" />
                  50% OFF
                </span>
              </span>
            </div>
          )}
        </div>

        {/* SAAS GRID */}
        {activeView === PricingType.SAAS && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto animate-fade-in-up">
              {SAAS_TIERS.map((tier) => {
                // Pricing Logic
                const isAnnual = billingInterval === 'annual';
                // 50% discount on annual plans
                const finalMonthlyPrice = isAnnual
                  ? Math.floor(tier.monthlyPrice * 0.5)
                  : tier.monthlyPrice;

                return (
                  <div
                    key={tier.name}
                    className={`
                      p-8 rounded-2xl border flex flex-col relative
                      ${tier.highlight
                        ? 'bg-gradient-to-b from-sky-900/30 to-slate-900 border-sky-500 shadow-2xl shadow-sky-900/20 transform md:scale-105 z-10'
                        : 'bg-slate-800 border-slate-700'}
                    `}
                  >
                    {tier.highlight && (
                      <div className="absolute top-0 right-0 bg-sky-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                        RECOMMENDED
                      </div>
                    )}

                    <h3 className={`text-xl font-bold text-white`}>
                      {tier.name}
                    </h3>

                    <div className="mt-4 flex flex-col">
                      <div className="flex items-baseline text-white">
                        <span className={`text-4xl font-bold ${tier.highlight ? 'text-sky-400' : ''}`}>
                          ${finalMonthlyPrice}
                        </span>
                        <span className="ml-1 text-slate-400">{tier.period}</span>
                      </div>

                      {isAnnual ? (
                        <div className="text-xs text-emerald-400 font-bold mt-1">
                          Billed ${finalMonthlyPrice * 12}/yr <span className="text-slate-500 line-through ml-1">${tier.monthlyPrice * 12}</span>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500 font-bold mt-1 h-4">
                          {/* Placeholder to keep alignment */}
                        </div>
                      )}

                      {!isAnnual && tier.strikePrice && (
                        <span className="text-lg font-bold text-slate-500 line-through absolute top-20 right-8">{tier.strikePrice}</span>
                      )}
                    </div>

                    <div className={`mt-6 border-t pt-6 ${tier.highlight ? 'border-sky-500/30' : 'border-slate-700'}`}>
                      <p className="text-xs text-slate-400 mb-6 min-h-[3rem]">{tier.description}</p>
                      <ul className="space-y-4 text-sm text-slate-300 flex-1">
                        {tier.features.map((feature, fIdx) => (
                          <li key={fIdx} className="flex items-center">
                            <Check className={`w-4 h-4 mr-3 flex-shrink-0 ${tier.highlight ? 'text-sky-400' : 'text-emerald-400'}`} />
                            <span className={feature.includes('Records') || feature.includes('Unlimited') ? 'font-bold text-white' : ''}>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-8">
                        <Button
                          variant={tier.highlight ? 'primary' : 'secondary'}
                          className="w-full"
                          onClick={() => handleCheckout(tier.name)}
                          disabled={loadingTier === tier.name}
                        >
                          {loadingTier === tier.name ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            tier.cta
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* TEAMS & ENTERPRISE SECTION */}
            <div className="max-w-4xl mx-auto mt-24 pt-16 border-t border-slate-800 animate-fade-in-up">
              <h3 className="text-2xl font-bold text-white text-center mb-4">Teams & Enterprise</h3>
              <p className="text-center text-slate-400 mb-12">Scalable solutions for organizations with multiple seats.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {TEAM_TIERS.map((tier) => (
                  <div key={tier.name} className="bg-slate-900/50 border border-slate-700 p-8 rounded-2xl flex flex-col hover:border-slate-600 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${tier.name === 'Enterprise' ? 'bg-amber-500/10' : 'bg-indigo-500/10'}`}>
                        {tier.name === 'Enterprise' ? <ShieldCheck className="w-6 h-6 text-amber-500" /> : <Building2 className="w-6 h-6 text-indigo-500" />}
                      </div>
                      <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                    </div>

                    <div className="flex items-baseline text-white mb-2">
                      <span className="text-3xl font-bold">{tier.price}</span>
                      <span className="ml-1 text-slate-400 text-sm">{tier.period}</span>
                    </div>
                    <p className="text-sm text-slate-400 mb-6">{tier.description}</p>

                    <ul className="space-y-3 text-sm text-slate-300 mb-8 flex-1">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center">
                          <Check className="w-4 h-4 text-slate-500 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => tier.name === 'Enterprise' ? handleContactSales() : handleCheckout(tier.name)}
                      disabled={loadingTier === tier.name}
                    >
                      {loadingTier === tier.name ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        tier.cta
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* API GRID */}
        {activeView === PricingType.API && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in max-w-6xl mx-auto">
            {API_PRICING.map((item, idx) => (
              <div key={idx} className="bg-slate-800 border border-slate-700 p-6 rounded-xl hover:border-sky-500 transition-colors group">
                <div className="text-sm text-sky-500 font-bold uppercase tracking-wider mb-2 group-hover:text-sky-400">{item.title}</div>
                <div className="text-4xl font-bold text-white mb-1">{item.price}</div>
                <div className="text-xs text-slate-500 mb-4">{item.unit}</div>
                <p className="text-sm text-slate-300">{item.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* DATA PACKS GRID */}
        {activeView === PricingType.DATA_PACKS && (
          <>
            {/* HOLIDAY50 Promotion Banner */}
            <div className="max-w-4xl mx-auto mb-8 animate-fade-in-up">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 shadow-xl">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-white">
                    <Tag className="w-6 h-6" />
                    <div>
                      <span className="font-bold text-lg">Limited Time Offer</span>
                      <p className="text-sm opacity-90">50% OFF all Data Packs with code HOLIDAY50</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2">
                    <label className="text-white text-sm font-medium">Coupon:</label>
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="bg-white/10 border border-white/30 rounded px-2 py-1 text-white placeholder-white/60 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-white/50"
                      placeholder="HOLIDAY50"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* State Availability Notice */}
            <div className="max-w-4xl mx-auto mb-8 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-white mb-2">Available States ({SELLABLE_STATES.length})</h4>
                  <div className="flex flex-wrap gap-2">
                    {SELLABLE_STATES.map(state => (
                      <span key={state.code} className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded" title={`${state.name}: ${state.records.toLocaleString()} records`}>
                        {state.code}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">13M+ professional license records across 15 major markets</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto animate-fade-in">
              {DATA_PACKS.map((pack) => (
                <div
                  key={pack.id}
                  className={`
                    relative p-6 rounded-xl border flex flex-col
                    ${pack.highlight
                      ? 'bg-gradient-to-b from-emerald-900/30 to-slate-900 border-emerald-500 shadow-xl shadow-emerald-900/20'
                      : 'bg-slate-800 border-slate-700'}
                  `}
                >
                  {pack.highlight && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      BEST VALUE
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-white mb-1">{pack.name}</h3>
                    <p className="text-xs text-slate-400">{pack.description}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline text-white">
                      <span className="text-3xl font-bold">${pack.price}</span>
                      <span className="ml-1 text-slate-500 text-xs">one-time</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {pack.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-300">
                        <Database className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    variant={pack.highlight ? 'primary' : 'outline'}
                    className="w-full"
                    onClick={() => handleCheckout(pack.id)}
                    disabled={loadingTier === pack.id}
                  >
                    {loadingTier === pack.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Buy Now'
                    )}
                  </Button>

                  {/* HOLIDAY50 Savings Display */}
                  <div className="mt-3 text-center">
                    <p className="text-xs text-emerald-400 font-medium">
                      Save ${Math.round(pack.price * 0.5)} with HOLIDAY50
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* State Selection Modal */}
      <StateSelectionModal
        isOpen={stateModalOpen}
        onClose={handleStateModalClose}
        onConfirm={handleStateSelectionConfirm}
        packName={pendingPack?.name || ''}
        requiredCount={pendingPack?.statesCount || 1}
        loading={loadingTier !== null}
      />
    </section>
  );
};

export default Pricing;